/**
 * Shared WebGPU / WASM device selection for Transformers.js models.
 *
 * onnxruntime-web 1.26 copies adapter.info onto GPUDevice.adapterInfo, then
 * reads adapterInfo.subgroupMinSize. Browsers that already run WebGPU often
 * still lack .info (or subgroup fields), so that copy is undefined and ORT
 * throws. Patch adapters/devices before session create; fall back to WASM
 * only if WebGPU init still fails.
 */

const DEFAULT_ADAPTER_INFO = {
    vendor: "",
    architecture: "",
    device: "",
    description: "",
    subgroupMinSize: 4,
    subgroupMaxSize: 128,
    isFallbackAdapter: false,
};

let ortAdapterInfoPatched = false;

export function hasWebGpuApi() {
    return typeof navigator !== "undefined"
        && !!navigator.gpu
        && typeof navigator.gpu.requestAdapter === "function";
}

/** @returns {Promise<Array<"webgpu"|"wasm">>} */
export async function preferredOnnxDevices() {
    ensureOrtWebGpuAdapterInfo();
    if (!hasWebGpuApi()) {
        return ["wasm"];
    }

    // Some mobile Chromium builds expose navigator.gpu but return no adapter.
    // Trying ORT's WebGPU backend in that state can poison its shared WASM
    // runtime before the fallback gets a chance to initialize.
    try {
        const adapter = await navigator.gpu.requestAdapter();
        if (adapter) {
            return ["webgpu", "wasm"];
        }
        console.warn("No WebGPU adapter is available; AI models will use WASM.");
    } catch (err) {
        console.warn("WebGPU adapter probe failed; AI models will use WASM.", err);
    }
    return ["wasm"];
}

/** RMBG uses fp32 everywhere; q8 produces visibly degraded alpha mattes. */
export function rmbgDtypeForDevice() {
    return "fp32";
}

/**
 * Make GPUAdapter.info / GPUDevice.adapterInfo always a complete object so
 * ORT never reads subgroupMinSize off undefined.
 */
export function ensureOrtWebGpuAdapterInfo() {
    if (ortAdapterInfoPatched || !hasWebGpuApi()) {
        return;
    }
    ortAdapterInfoPatched = true;

    if (typeof GPUAdapter !== "undefined") {
        const originalRequestDevice = GPUAdapter.prototype.requestDevice;
        GPUAdapter.prototype.requestDevice = async function requestDevicePatched(...args) {
            const device = await originalRequestDevice.apply(this, args);
            stampDeviceAdapterInfo(device, adapterInfoFrom(this));
            return device;
        };
    }

    const gpu = navigator.gpu;
    const originalRequestAdapter = gpu.requestAdapter.bind(gpu);
    gpu.requestAdapter = async function requestAdapterPatched(...args) {
        const adapter = await originalRequestAdapter(...args);
        stampAdapterInfo(adapter);
        return adapter;
    };
}

function completeAdapterInfo(info) {
    const src = info && typeof info === "object" ? info : {};
    return {
        vendor: String(src.vendor ?? DEFAULT_ADAPTER_INFO.vendor),
        architecture: String(src.architecture ?? DEFAULT_ADAPTER_INFO.architecture),
        device: String(src.device ?? DEFAULT_ADAPTER_INFO.device),
        description: String(src.description ?? DEFAULT_ADAPTER_INFO.description),
        subgroupMinSize: Number.isFinite(src.subgroupMinSize)
            ? src.subgroupMinSize
            : DEFAULT_ADAPTER_INFO.subgroupMinSize,
        subgroupMaxSize: Number.isFinite(src.subgroupMaxSize)
            ? src.subgroupMaxSize
            : DEFAULT_ADAPTER_INFO.subgroupMaxSize,
        isFallbackAdapter: !!src.isFallbackAdapter,
    };
}

function adapterInfoFrom(adapter) {
    if (!adapter) {
        return completeAdapterInfo(null);
    }
    return completeAdapterInfo(adapter.info ?? adapter.adapterInfo);
}

function defineOwn(target, key, value) {
    if (!target) {
        return;
    }
    try {
        Object.defineProperty(target, key, {
            configurable: true,
            enumerable: true,
            writable: true,
            value,
        });
    } catch {
        try {
            target[key] = value;
        } catch {
            /* native WebGPU objects can reject both */
        }
    }
}

function stampAdapterInfo(adapter) {
    if (!adapter) {
        return;
    }
    const info = adapterInfoFrom(adapter);
    if (adapter.info == null) {
        defineOwn(adapter, "info", info);
    }
    defineOwn(adapter, "adapterInfo", info);
}

function stampDeviceAdapterInfo(device, info) {
    if (!device) {
        return;
    }
    const complete = completeAdapterInfo(info ?? device.adapterInfo ?? device.info);
    if (device.adapterInfo == null || typeof device.adapterInfo.subgroupMinSize !== "number") {
        defineOwn(device, "adapterInfo", complete);
    }
}
